/**
 * Lo que se pide en «Pedir vacaciones», en tramos.
 *
 * Desde el 13/10/2026 (Norman: «que también se puedan pedir independientes,
 * ejemplo el 15 y otro día el 20, solo esos dos») hay dos formas de elegir:
 * días seguidos (primer y último día) o días sueltos. En el servidor nada
 * cambia: cada solicitud sigue siendo un tramo con sus dos fechas y su
 * FRC-019. Los días sueltos se juntan aquí en tramos y se manda una
 * solicitud por cada uno.
 */

/** Un tramo de días seguidos: lo que el servidor recibe como una solicitud. */
export interface Tramo {
  desde: Date;
  hasta: Date;
  /** Días hábiles que descuenta. */
  dias: number;
}

/** Cuántos tramos se pueden mandar de una vez (uno es una solicitud con su FRC-019). */
export const MAX_TRAMOS = 8;

/**
 * Junta días sueltos en tramos. Dos días van en el mismo tramo si entre
 * ellos no hay ningún día hábil sin elegir —el fin de semana y los festivos
 * no separan: viernes y lunes son un tramo— y si son del mismo periodo
 * (`clave`). Un tramo que cruzara el aniversario se descontaría entero del
 * periodo en que empieza, y con días sueltos cada día se espera en el suyo.
 */
export function agrupaEnTramos(
  dias: Date[],
  esHabil: (d: Date) => boolean,
  clave: (d: Date) => unknown = () => null,
): Tramo[] {
  const orden = [...new Map(dias.map((d) => [sinHora(d).getTime(), sinHora(d)])).values()].sort(
    (a, b) => a.getTime() - b.getTime(),
  );
  const tramos: Tramo[] = [];
  for (const d of orden) {
    const ultimo = tramos.at(-1);
    if (ultimo && clave(ultimo.hasta) === clave(d) && sinHabilesEntre(ultimo.hasta, d, esHabil)) {
      ultimo.hasta = d;
      ultimo.dias++;
    } else {
      tramos.push({ desde: d, hasta: d, dias: 1 });
    }
  }
  return tramos;
}

/** Los días hábiles de un tramo, uno por uno: lo que queda elegido al pasar
 *  de «días seguidos» a «días sueltos». */
export function diasHabilesDe(desde: Date, hasta: Date, esHabil: (d: Date) => boolean): Date[] {
  const dias: Date[] = [];
  const fin = sinHora(hasta).getTime();
  const cursor = sinHora(desde);
  // Con tope: nadie pide más de un año de corrido.
  for (let i = 0; i < 400 && cursor.getTime() <= fin; i++) {
    if (esHabil(cursor)) {
      dias.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return dias;
}

/** La cuenta de un periodo con lo que se está pidiendo. */
export interface CuentaDePeriodo<P> {
  periodo: P;
  /** Días que se le piden: los de los tramos que empiezan en él. */
  pide: number;
  /** Lo que le queda para eso: lo suyo, menos lo que estos mismos tramos se
   *  llevan de periodos anteriores (que ya no se le acumula). null si no
   *  tiene días asignados. */
  tiene: number | null;
  /** tiene − pide. */
  quedarian: number | null;
}

export interface CuentaDelPedido<P> {
  /** Los periodos a los que van los tramos, del primero al último. */
  periodos: CuentaDePeriodo<P>[];
  /** El primero al que no le alcanza. */
  excede: CuentaDePeriodo<P> | null;
  /** Un periodo posterior, sin tramos, que quedaría en negativo: ya tenía
   *  días pedidos contando con lo que se le iba a acumular. */
  negativo: { periodo: P; quedaria: number } | null;
}

export interface ReglasDeSaldo<P> {
  /** El periodo del que se descuenta un tramo que empieza ese día. */
  periodoDe(dia: Date): P | null;
  /** Lo que le queda a ese periodo (restantes + acumulado), o null. */
  quedan(periodo: P): number | null;
  /** El periodo al que se le acumula lo que sobre de este; null si no hay
   *  o si su acumulado lo fijó RH (entonces lo de antes ya no le llega). */
  siguiente(periodo: P): P | null;
}

/**
 * La misma cuenta que hace el servidor al recibir cada solicitud
 * (VacacionBuss.exigeSaldoSuficiente), para decirlo antes de la firma: cada
 * tramo se descuenta del periodo en que empieza, y lo que se toma de un
 * periodo ya no se acumula a los que siguen.
 */
export function cuentaDelPedido<P>(tramos: Tramo[], reglas: ReglasDeSaldo<P>): CuentaDelPedido<P> {
  const pide = new Map<P, number>();
  for (const t of tramos) {
    const p = reglas.periodoDe(t.desde);
    if (p !== null) {
      pide.set(p, (pide.get(p) || 0) + t.dias);
    }
  }

  // Lo que cada periodo deja de recibir por lo pedido en los anteriores.
  const deMenos = new Map<P, number>();
  for (const [p, dias] of pide) {
    let actual = p;
    // Con tope: nadie tiene más de un puñado de periodos abiertos.
    for (let i = 0; i < 20 && reglas.quedan(actual) !== null; i++) {
      const sig = reglas.siguiente(actual);
      if (sig === null) {
        break;
      }
      deMenos.set(sig, (deMenos.get(sig) || 0) + dias);
      actual = sig;
    }
  }

  const periodos = [...pide].map(([periodo, dias]): CuentaDePeriodo<P> => {
    const quedan = reglas.quedan(periodo);
    const tiene = quedan === null ? null : quedan - (deMenos.get(periodo) || 0);
    return { periodo, pide: dias, tiene, quedarian: tiene === null ? null : tiene - dias };
  });

  let negativo: CuentaDelPedido<P>["negativo"] = null;
  for (const [periodo, menos] of deMenos) {
    const quedan = reglas.quedan(periodo);
    if (!pide.has(periodo) && quedan !== null && quedan - menos < 0) {
      negativo = { periodo, quedaria: quedan - menos };
      break;
    }
  }

  return {
    periodos,
    excede: periodos.find((c) => c.quedarian !== null && c.quedarian < 0) || null,
    negativo,
  };
}

function sinHora(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Si de `a` a `b` (sin contarlos) no hay ningún día hábil. */
function sinHabilesEntre(a: Date, b: Date, esHabil: (d: Date) => boolean): boolean {
  const cursor = new Date(a.getFullYear(), a.getMonth(), a.getDate() + 1);
  // Con tope: no hay más de unos días seguidos sin trabajo.
  for (let i = 0; i < 15 && cursor < b; i++) {
    if (esHabil(cursor)) {
      return false;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return cursor >= b;
}
